/**
 * RepBuddy - Hunter Service
 * Calls backend Lead411 routes.
 * Results now come back pre-verified as [{ contact, result }] pairs.
 * All API keys live on the backend - no keys needed here.
 */

import { supabase } from '../lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// An error whose message is written for the customer. The chat shows it as is; any other
// error is replaced there by a plain line (see chatFailure in VerifierPage).
function friendlyError(message) {
  const e = new Error(message);
  e.userFacing = true;
  return e;
}

// ─── Auth helper ──────────────────────────────────────────────────────────────

async function authedHeaders() {
  const base = { 'Content-Type': 'application/json' };
  if (!supabase) return base;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return base;

  return { ...base, Authorization: `Bearer ${session.access_token}` };
}

// ─── Classify intent + get conversational response ───────────────────────────

export async function classifyAndRespond(message, sellerProfile = null, chatContext = null) {
  const body = { message, sellerProfile };
  if (chatContext) body.chatContext = chatContext;
  const r = await fetch(`${BACKEND_URL}/api/chat/respond`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify(body),
  });
  if (!r.ok) return { intent: 'search' };
  return r.json().catch(() => ({ intent: 'search' }));
}

// ─── Streaming chat calls ─────────────────────────────────────────────────────
// Both chat stream routes speak the same SSE dialect as the search stream: every
// line is `data: {json}` with a `type` field. This reads one of them to its `done`
// event and returns that event's payload (minus `type`); intermediate events go to
// onEvent. Resolves null when the stream never produced a byte, so the caller can
// fall back to the non-streaming route; throws when it died part-way, because
// re-running a request that already half-answered is never the right fix.
async function readChatStream(path, body, onEvent = () => {}, opts = {}) {
  const ctrl = new AbortController();
  const onExternalAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onExternalAbort, { once: true });
  if (opts.signal?.aborted) ctrl.abort();

  let sawAnyBytes = false;
  try {
    const response = await fetch(`${BACKEND_URL}${path}`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify(body),
      signal:  ctrl.signal,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Stream request failed' }));
      throw new Error(err.error || `Stream failed with status ${response.status}`);
    }

    return await new Promise((resolve, reject) => {
      let final = null;
      let failure = null;
      // Idle, not total: the model can legitimately take a few seconds before the
      // first token, and the server pings every 15s while it waits.
      const IDLE_TIMEOUT_MS = 45_000;
      let timer = null;
      const resetIdle = () => {
        clearTimeout(timer);
        timer = setTimeout(() => { ctrl.abort(); reject(new Error('Chat timed out.')); }, IDLE_TIMEOUT_MS);
      };
      resetIdle();

      readSSEStream(response, (event) => {
        sawAnyBytes = true;
        resetIdle();
        if (event.type === 'done') {
          const { type, ...rest } = event;
          final = rest;
        } else if (event.type === 'error') {
          failure = new Error(event.message || 'Chat failed');
        } else {
          onEvent(event);
        }
      }, () => { sawAnyBytes = true; resetIdle(); }).then(() => {
        clearTimeout(timer);
        if (final) resolve(final);
        else reject(failure || new Error('Stream ended without result'));
      }).catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
    });
  } catch (err) {
    if (opts.signal?.aborted) {
      const cancelled = new Error('Stopped.');
      cancelled.cancelled = true;
      throw cancelled;
    }
    if (!sawAnyBytes) return null;   // streaming unavailable: caller falls back
    throw err;
  } finally {
    opts.signal?.removeEventListener('abort', onExternalAbort);
  }
}

// Streaming twin of classifyAndRespond. onEvent receives
//   { type: 'intent', intent }   as soon as the model has decided what this is
//   { type: 'delta',  text }     pieces of the reply, already decoded
// and the promise resolves to the same object classifyAndRespond returns.
export async function classifyAndRespondStream(message, sellerProfile = null, chatContext = null, onEvent = () => {}, opts = {}) {
  const body = { message, sellerProfile };
  if (chatContext) body.chatContext = chatContext;
  const result = await readChatStream('/api/chat/respond/stream', body, onEvent, opts);
  if (result) return result;
  // The stream never started (older backend, proxy without SSE). Same answer, one blob.
  return classifyAndRespond(message, sellerProfile, chatContext);
}

// After a search: RepBuddy's take on what came back, plus clickable next moves.
// onEvent receives { type: 'delta', text }; resolves to { narration, suggestions }.
// Resolves null when unavailable - the caller renders nothing rather than an error,
// because a missing debrief must never look like a failed search.
// `opts.kind` names the flow (search, person, batch, lookalike, enrich, verify, research)
// so the server picks the matching rules; the backend defaults to search.
export async function searchDebrief(summary, sellerProfile = null, recentMessages = [], activeGoal = null, onEvent = () => {}, opts = {}) {
  const body = { kind: opts.kind || 'search', summary, sellerProfile, recentMessages, active_goal: activeGoal, memory: opts.memory || null };
  return readChatStream('/api/chat/search-debrief', body, onEvent, opts);
}

// One step of the tool-calling loop. `turn` is the assistant/user pairs so far this message
// (each a tool_use and its tool_result); the page executes whatever tool the step returns and
// calls again with the result. onEvent receives the same `intent` and `delta` events as the
// classifier stream when the model replies instead. Resolves to
//   { stop_reason, content, toolCall: { id, name, input, error? } | null, reply | null }
// or null when the stream never started, which the caller treats as "use the classifier".
export async function agentStep({ message, sellerProfile = null, chatContext = null, turn = [] }, onEvent = () => {}, opts = {}) {
  const body = { message, sellerProfile, turn };
  if (chatContext) body.chatContext = chatContext;
  return readChatStream('/api/chat/agent/step', body, onEvent, opts);
}

export async function summarizeChat(messages, existing_summary = null) {
  const body = { messages };
  if (existing_summary) body.existing_summary = existing_summary;
  const r = await fetch(`${BACKEND_URL}/api/chat/summarize`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify(body),
  });
  if (!r.ok) return null;
  const d = await r.json().catch(() => null);
  return d?.summary || null;
}

// ─── Parse natural language into search criteria ──────────────────────────────

// `memory` is the compact learned-defaults summary (lib/memory.js); the parser uses it only
// where the message and the profile are both silent, and for "the usual".
// The pre-search card's location box: "Austin", "Austin, TX", "bay area" → a city with its state,
// or { kind: 'unknown' }. States are resolved on the client first; this is for everything else.
export async function resolvePlace(q) {
  try {
    const r = await fetch(`${BACKEND_URL}/api/geo/place?q=${encodeURIComponent(String(q || '').slice(0, 60))}`, {
      headers: await authedHeaders(),
    });
    if (!r.ok) return { kind: 'unknown', states: [], city: '', label: '' };
    return await r.json();
  } catch {
    return { kind: 'unknown', states: [], city: '', label: '' };
  }
}

export async function parseHunterQuery(message, history = [], profile = null, activeConstraints = null, memory = null) {
  const r = await fetch(`${BACKEND_URL}/api/hunter/parse`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ message, history, profile, activeConstraints, memory }),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Parse request failed' }));
    throw new Error(err.error || `Parse failed with status ${r.status}`);
  }

  const parsed = await r.json();
  console.log('Hunter parsed criteria:', parsed);
  return parsed;
}

// ─── Batch search - SSE streaming variant ────────────────────────────────────
// Calls /api/hunter/batch/stream and invokes onEvent for each SSE event.
// Events: start { total } · progress { current, total, company, phase, found? }
//         done { total, companiesFound, contactsFound, notFound, results }
// Returns the final done-event payload, or throws on error.

// bypassCache/excludeEmails mirror the single-search body. Without them the batch had no way to
// offer "new pool vs existing" at all - it always read whatever the server had cached.
// `opts.signal` is the user's Stop button, same as the single search. Aborting closes the socket,
// which the backend now watches - so Stop actually stops the Lead411 unlocks and Gemini calls.
export async function batchSearchStream({ companies, titleKeyword, titleKeywords, countryCode = 'US', countPerCompany = 1, profileFilters = null, bypassCache = false, excludeEmails = [], newPool = false, backfillExisting = false }, onEvent = () => {}, opts = {}) {
  const ctrl = new AbortController();
  const onExternalAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onExternalAbort, { once: true });
  if (opts.signal?.aborted) ctrl.abort();

  const startedAt = Date.now();
  const response = await fetch(`${BACKEND_URL}/api/hunter/batch/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    // newPool alongside excludeEmails for the same reason as the single search: the email list
    // is only what this chat holds, the flag is what reaches the server's full delivery history.
    body:    JSON.stringify({ companies, titleKeyword, titleKeywords, countryCode, countPerCompany, profileFilters, bypassCache, excludeEmails, newPool, backfillExisting }),
    signal:  ctrl.signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Batch stream request failed' }));
    throw new Error(err.error || `Batch stream failed with status ${response.status}`);
  }

  return new Promise((resolve, reject) => {
    let finalResult = null;
    let blocked     = false;
    let streamError = null;

    // Rows accumulated from the per-company events.
    //
    // Contacts used to arrive only in the terminal 'done' event, which made every batch
    // all-or-nothing: a 20-company run that lost its socket at company 19 delivered zero
    // contacts, and the user was still billed for the 19. The server now ships each company as
    // it finishes, so whatever got here is real, paid for, and worth handing over even if the
    // rest never arrives.
    const partialRows = [];
    const processedCompanyNames = [];
    let companiesFound = 0;
    let contactsFound  = 0;
    let lastCredits    = null;
    let total          = (companies || []).length;
    let partialReason  = 'disconnect';

    // Idle-based, measured from the last byte - not a flat cap on total duration.
    //
    // It used to be a flat 600s. That is not a safety net, it is a ceiling on how big a batch
    // can be: at ~32s per company a 20-company run took 641s and was rejected 41 seconds from
    // the finish line, and a 50-company run (~27 minutes) could never have succeeded at all.
    // The single-search path was moved off a flat cap for the same reason - see the note there.
    //
    // The backend pings every 15s, so a batch that is still working always resets this; only
    // genuine silence trips it. 120s rather than the search path's 90s because one company can
    // legitimately sit in a 45s Gemini fallback behind a stack of 15s ZeroBounce probes.
    const IDLE_TIMEOUT_MS = 120_000;
    let streamTimeout = null;
    const failOnIdle = () => {
      partialReason = 'timeout';
      // Abort as well as give up. Rejecting alone left the reader looping and the backend
      // running - and the backend, which now watches for the disconnect, only stops spending
      // once the socket actually closes.
      ctrl.abort();
    };
    const resetIdleTimer = () => {
      clearTimeout(streamTimeout);
      streamTimeout = setTimeout(failOnIdle, IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();

    // One exit for every way this stream can end, because the decision is the same each time:
    // a complete result wins, otherwise anything we already received beats throwing it away.
    const settle = (readerErr) => {
      clearTimeout(streamTimeout);
      const elapsed = Math.round((Date.now() - startedAt) / 1000);

      if (blocked) {
        console.warn(`[batch-sse] blocked after ${elapsed}s - out of credits`);
        return reject(new Error("You've used all your lead credits. Open Account → Usage to see your balance."));
      }
      if (finalResult) {
        console.log(`[batch-sse] complete after ${elapsed}s - ${finalResult.contactsFound ?? 0} contacts`);
        return resolve(finalResult);
      }
      if (partialRows.length > 0) {
        if (opts.signal?.aborted) partialReason = 'stopped';
        console.warn(`[batch-sse] partial after ${elapsed}s (${partialReason}) - ${processedCompanyNames.length}/${total} companies, ${contactsFound} contacts`);
        return resolve({
          total,
          companiesFound,
          contactsFound,
          notFound: partialRows.filter(r => r.status === 'not_found' || r.status === 'error').length,
          results:  partialRows,
          found:    companiesFound,
          credits:  lastCredits,
          partial:  true,
          partialReason,
          companiesProcessed: processedCompanyNames.length,
          processedCompanyNames,
        });
      }
      // Nothing arrived at all - there is genuinely nothing to hand over.
      const err = streamError || readerErr || new Error('Batch stream ended without result');
      console.warn(`[batch-sse] failed after ${elapsed}s - ${err.message}`);
      return reject(err);
    };

    readSSEStream(response, (event) => {
      resetIdleTimer();
      onEvent(event);
      if (event.type === 'start') {
        if (typeof event.total === 'number') total = event.total;
      } else if (event.type === 'company') {
        partialRows.push(...(event.rows || []));
        processedCompanyNames.push(event.company);
        if (typeof event.companiesFound === 'number') companiesFound = event.companiesFound;
        if (typeof event.contactsFound  === 'number') contactsFound  = event.contactsFound;
        if (event.credits) lastCredits = event.credits;
      } else if (event.type === 'blocked') {
        // Server refused to start the batch - no lead credits remaining.
        blocked = true;
      } else if (event.type === 'done') {
        finalResult = event;
      } else if (event.type === 'error') {
        // Recorded, not thrown. If companies already arrived, those rows were delivered and
        // charged for; settle() hands them over rather than discarding them over a late failure.
        partialReason = 'error';
        streamError = new Error(event.message || 'Batch search failed');
      }
    }, resetIdleTimer)
      .then(()    => settle(null))
      .catch(err  => settle(err))
      .finally(() => opts.signal?.removeEventListener('abort', onExternalAbort));
  });
}

// ─── Batch search - auto-chunked for large files ─────────────────────────────
// The backend caps one request at 50 companies; this wrapper is what lets a 112-row file run
// hands-off instead of the user splitting it by hand. Chunks run sequentially - the backend
// already works several companies at once within a chunk - and every chunk after the first
// carries the delivered addresses as excludeEmails, the same anti-re-delivery contract the
// "Finish remaining" flow uses.
//
// Resolves with one merged done-shaped payload. If any chunk comes up short (Stop, timeout,
// disconnect, error after earlier chunks delivered), the merged payload is marked partial with
// processedCompanyNames covering everything that finished - so the existing name-based
// "Finish remaining" works across chunk boundaries unchanged.
export const BATCH_CHUNK_SIZE = 50;

export async function batchSearchChunked(params, onEvent = () => {}, opts = {}) {
  const all = params.companies || [];
  if (all.length <= BATCH_CHUNK_SIZE) return batchSearchStream(params, onEvent, opts);

  const total = all.length;
  let excludeEmails = [...(params.excludeEmails || [])];
  const rows = [];
  const processedCompanyNames = [];
  let lastCredits     = null;
  let completedOffset = 0;

  const finish = (partialReason) => {
    const companiesWithResults = new Set(rows.filter(r => r.status === 'found').map(r => r.company));
    const partial = processedCompanyNames.length < total;
    return {
      total,
      companiesFound: companiesWithResults.size,
      contactsFound:  rows.filter(r => r.status === 'found').length,
      notFound:       rows.filter(r => r.status === 'not_found' || r.status === 'error').length,
      results:        rows,
      found:          companiesWithResults.size,
      credits:        lastCredits,
      processedCompanyNames,
      companiesProcessed: processedCompanyNames.length,
      ...(partial ? { partial: true, partialReason: partialReason || 'disconnect' } : {}),
    };
  };

  for (let start = 0; start < all.length; start += BATCH_CHUNK_SIZE) {
    if (opts.signal?.aborted) return finish('stopped');
    const chunk = all.slice(start, start + BATCH_CHUNK_SIZE);

    // Rebase per-chunk numbers to the whole file, so the ticker reads 63/112 rather than
    // restarting at 13/50 on every chunk.
    const shim = (event) => {
      const e = { ...event };
      if (typeof e.total === 'number') e.total = total;
      if (event.type === 'progress') {
        if (typeof event.current   === 'number') e.current   = Math.min(completedOffset + event.current, total);
        if (typeof event.completed === 'number') e.completed = completedOffset + event.completed;
      } else if (event.type === 'company' && typeof event.index === 'number') {
        e.index = start + event.index;
      }
      onEvent(e);
    };

    let res;
    try {
      res = await batchSearchStream({ ...params, companies: chunk, excludeEmails }, shim, opts);
    } catch (err) {
      // This chunk delivered nothing at all. With no earlier rows that is a plain failure;
      // with earlier rows it is a partial - replacing paid-for results with an error message
      // would lose contacts the user was already charged for.
      if (rows.length === 0) throw err;
      return finish(opts.signal?.aborted ? 'stopped' : 'error');
    }

    rows.push(...(res.results || []));
    if (res.credits) lastCredits = res.credits;

    if (res.partial) {
      // Whatever stopped this chunk (Stop, timeout, disconnect) applies to the batch -
      // starting the next chunk against a dead tab or a pressed Stop button would be wrong.
      processedCompanyNames.push(...(res.processedCompanyNames || []));
      return finish(res.partialReason);
    }

    // A complete chunk processed every company in it. Same label convention the backend's
    // company events and the resume matcher use: name, else domain.
    processedCompanyNames.push(...chunk.map((c, i) => (c.name || c.domain || `Company ${i + 1}`)));
    completedOffset += chunk.length;
    excludeEmails = [
      ...excludeEmails,
      ...(res.results || [])
        .filter(r => r.status === 'found' && r?.result?.email)
        .map(r => r.result.email),
    ];
  }

  return finish(null);
}

// ─── Look-alike companies - SSE streaming ────────────────────────────────────
// Calls /api/lookalike/stream. Events: start { seed } · phase { phase, message } ·
// progress { current, total } · done { seed, results, requested, delivered, shortfallReason } ·
// error { message }. Resolves with the final 'done' payload, or throws on error/abort.
// `opts.signal` is the user's Stop button - aborting closes the socket, which the backend watches
// to stop spending on discovery/classification.
export async function lookalikeStream({ seedName, seedDomain = '', count = 25, basis = 'buyer_fit', seedProfile = null }, onEvent = () => {}, opts = {}) {
  const ctrl = new AbortController();
  const onExternalAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onExternalAbort, { once: true });
  if (opts.signal?.aborted) ctrl.abort();

  const response = await fetch(`${BACKEND_URL}/api/lookalike/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ seedName, seedDomain, count, basis, seedProfile }),
    signal:  ctrl.signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Look-alike stream request failed' }));
    throw new Error(err.error || `Look-alike stream failed with status ${response.status}`);
  }

  return new Promise((resolve, reject) => {
    let finalResult = null;
    let streamError = null;

    // Idle-based, same reasoning as the batch stream: discovery + classification can legitimately
    // run for minutes, and the backend pings every 15s, so only genuine silence trips this.
    const IDLE_TIMEOUT_MS = 120_000;
    let streamTimeout = null;
    const resetIdleTimer = () => {
      clearTimeout(streamTimeout);
      streamTimeout = setTimeout(() => ctrl.abort(), IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();

    const stopped = () => { const c = new Error('Stopped.'); c.cancelled = true; return c; };

    readSSEStream(response, (event) => {
      resetIdleTimer();
      onEvent(event);
      if (event.type === 'done') {
        clearTimeout(streamTimeout);
        finalResult = event;
      } else if (event.type === 'error') {
        streamError = new Error(event.message || 'Look-alike search failed');
      }
    }, resetIdleTimer)
      .then(() => {
        clearTimeout(streamTimeout);
        if (finalResult) return resolve(finalResult);
        if (opts.signal?.aborted) return reject(stopped());
        return reject(streamError || new Error('Look-alike stream ended without result'));
      })
      .catch((err) => {
        clearTimeout(streamTimeout);
        if (opts.signal?.aborted) return reject(stopped());
        return reject(streamError || err);
      })
      .finally(() => opts.signal?.removeEventListener('abort', onExternalAbort));
  });
}

// ─── Companies by category - SSE streaming ───────────────────────────────────
// Calls /api/companies/discover/stream: a free list of companies by industry, place and stage
// ("5 startups in Austin"). Same shape as the look-alike stream.
// Events: start { criteria } · phase { phase, message } · company { company, index }
//         progress { current, total } · done { criteria, results, requested, delivered, shortfallReason, rejected }
export async function companyDiscoveryStream({ count = 10, industry = '', location = '', stage = '', extra = '', exclude = [] }, onEvent = () => {}, opts = {}) {
  const ctrl = new AbortController();
  const onExternalAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onExternalAbort, { once: true });
  if (opts.signal?.aborted) ctrl.abort();

  const response = await fetch(`${BACKEND_URL}/api/companies/discover/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ count, industry, location, stage, extra, exclude }),
    signal:  ctrl.signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Company list request failed' }));
    throw new Error(err.error || `Company list failed with status ${response.status}`);
  }

  return new Promise((resolve, reject) => {
    let finalResult = null;
    let streamError = null;

    const IDLE_TIMEOUT_MS = 120_000;
    let streamTimeout = null;
    const resetIdleTimer = () => {
      clearTimeout(streamTimeout);
      streamTimeout = setTimeout(() => ctrl.abort(), IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();

    const stopped = () => { const c = new Error('Stopped.'); c.cancelled = true; return c; };

    readSSEStream(response, (event) => {
      resetIdleTimer();
      onEvent(event);
      if (event.type === 'done') {
        clearTimeout(streamTimeout);
        finalResult = event;
      } else if (event.type === 'error') {
        streamError = new Error(event.message || 'Company list failed');
      }
    }, resetIdleTimer)
      .then(() => {
        clearTimeout(streamTimeout);
        if (finalResult) return resolve(finalResult);
        if (opts.signal?.aborted) return reject(stopped());
        return reject(streamError || new Error('Company list stream ended without result'));
      })
      .catch((err) => {
        clearTimeout(streamTimeout);
        if (opts.signal?.aborted) return reject(stopped());
        return reject(streamError || err);
      })
      .finally(() => opts.signal?.removeEventListener('abort', onExternalAbort));
  });
}

// ─── Named Contacts Batch - SSE streaming ────────────────────────────────────
// Calls /api/named-contacts-batch/stream with an array of persons.
// Events: start { total } · progress { current, total, name }
//         done { total, found, notFound, results }

export async function namedContactsBatchStream({ persons }, onEvent = () => {}) {
  const response = await fetch(`${BACKEND_URL}/api/named-contacts-batch/stream`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ persons }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Named contacts batch failed' }));
    throw new Error(err.error || `Named contacts batch failed with status ${response.status}`);
  }

  return new Promise((resolve, reject) => {
    let finalResult = null;

    // Idle-based for the same reason as the batch stream above: a flat cap on total duration is
    // a ceiling on how many people you can look up, not a safety net. The route now sends the
    // same 15s keep-alive pings, so only genuine silence trips this.
    const IDLE_TIMEOUT_MS = 120_000;
    let streamTimeout = null;
    const resetIdleTimer = () => {
      clearTimeout(streamTimeout);
      streamTimeout = setTimeout(() => {
        reject(friendlyError('Named contacts batch timed out. Please try again.'));
      }, IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();

    readSSEStream(response, (event) => {
      resetIdleTimer();
      onEvent(event);
      if (event.type === 'done') {
        clearTimeout(streamTimeout);
        finalResult = event;
      } else if (event.type === 'error') {
        clearTimeout(streamTimeout);
        reject(new Error(event.message || 'Named contacts batch failed'));
      }
    }, resetIdleTimer).then(() => {
      clearTimeout(streamTimeout);
      if (finalResult) resolve(finalResult);
      else reject(new Error('Stream ended without result'));
    }).catch(err => {
      clearTimeout(streamTimeout);
      reject(err);
    });
  });
}

// ─── Batch search - N contacts per company for a given designation ───────────
// companies: [{ name, domain, state?, designation?, count? }]
// countPerCompany: global default count (overridden per-company by company.count)

export async function batchSearch({ companies, titleKeyword, titleKeywords, countryCode = 'US', countPerCompany = 1, profileFilters = null }) {
  const r = await fetch(`${BACKEND_URL}/api/hunter/batch`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ companies, titleKeyword, titleKeywords, countryCode, countPerCompany, profileFilters }),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Batch request failed' }));
    throw new Error(err.error || `Batch failed with status ${r.status}`);
  }

  return r.json(); // { total, companiesFound, contactsFound, notFound, results }
}

// ─── LinkdAPI - Employment Verification ──────────────────────────────────────
// Calls the backend which calls LinkdAPI /profile/full.
// Returns { confirmed, currentCompany, currentTitle, linkedinUrl, checkedAt }.

export async function verifyEmployment(linkedinUsername, expectedCompany = '', name = '') {
  const r = await fetch(`${BACKEND_URL}/api/linkdapi/verify`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ linkedinUsername, expectedCompany, name }),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Employment verify request failed' }));
    throw new Error(err.error || `Employment verify failed with status ${r.status}`);
  }

  return r.json();
}

// ─── SSE stream helper ────────────────────────────────────────────────────────
// Reads a text/event-stream response and calls onEvent for each parsed data line.

// onActivity fires on every chunk that arrives, whether or not it parses into an event.
//
// That distinction matters: the server's keep-alives are `: ping`, an SSE *comment*, and the
// parser below only surfaces `data:` lines. A caller measuring liveness by events alone would
// see a long-running search as silent and abandon it — which is exactly what used to happen.
// Bytes arriving is the honest signal that the stream is still alive.
async function readSSEStream(response, onEvent, onActivity = () => {}) {
  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer    = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      onActivity();
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            onEvent(parsed);
          } catch {}
        }
      }
    }

    // Flush any data remaining in the buffer after the stream closes.
    // This handles the case where the final chunk doesn't end with \n\n.
    if (buffer.trim()) {
      for (const line of buffer.split('\n')) {
        if (line.startsWith('data: ')) {
          try { onEvent(JSON.parse(line.slice(6))); } catch {}
        }
      }
    }
  } finally {
    // An aborted fetch rejects the pending read, which is what ends the loop above - but the lock
    // outlives the rejection, and a held lock on a dead body is a leak per abandoned search.
    try { reader.releaseLock(); } catch {}
  }
}

// ─── Stream variant of searchContacts ────────────────────────────────────────
// Calls /api/hunter/search/stream and invokes onEvent for each SSE event.
// Returns the final { contacts, total, source, quota } from the 'done' event,
// or throws on error.

// `opts.signal` is the user's Stop button. Aborting the fetch closes the socket, which is what
// the backend now watches to cancel the search - so pressing Stop stops the Lead411 unlocks and
// the Gemini calls rather than merely hiding them.
export async function searchContactsStream(criteria, profileFilters = null, sellerProfile = null, onEvent = () => {}, opts = {}) {
  const payload = {
    firstName:                criteria.firstName                || '',
    lastName:                 criteria.lastName                 || '',
    titleKeyword:             criteria.titleKeyword             || '',
    companyDomain:            criteria.companyDomain            || '',
    companyName:              criteria.companyName              || '',
    countryCode:              criteria.countryCode              || 'US',
    stateArray:               criteria.stateArray               || '',
    filterLevel:              criteria.filterLevel              || '',
    limit:                    criteria.limit                    || 10,
    // Whether that number is theirs or ours. The payload is a whitelist, so without this line the
    // flag never arrives and the delivery copy goes back to telling people they asked for 10.
    limitStated:              criteria.limitStated              || false,
    profileFilters,
    excludeEmails:            criteria.excludeEmails            || [],
    bypassCache:              criteria.bypassCache              || false,
    // "New pool". excludeEmails only carries what THIS chat is holding, so on a fresh chat it is
    // empty; this flag is what tells the server to exclude everything it has ever delivered to
    // this user, across every session. The payload is a whitelist - omit it here and the flag
    // never reaches the backend.
    newPool:                  criteria.newPool                  || false,
    sellerProfile,
    // User-specified diversity preferences from in-chat clarification
    userPerCompanyCap:        criteria.userPerCompanyCap        ?? null,
    userIndustryDistribution: criteria.userIndustryDistribution ?? null,
    // Explicit industry named in query - overrides seller profile industries
    industryOverride:         criteria.industryOverride         || null,
    // 'person' when the place in the request is about where the contacts live (confirmed on the
    // pre-search card). The payload is a whitelist; without this line the ask never arrives.
    locationType:             criteria.locationType             || '',
    // Explicit company size named in query - overrides profile companySize
    employeeRange:            criteria.employeeRange            || null,
    // Explicit revenue figure named in query - overrides profile revenue
    revenueRange:             criteria.revenueRange             || null,
    // A stage named in words ("startups") and a city ("Austin"). The payload is a whitelist;
    // without these two lines the words the parser read never reach the engine.
    companyStage:             criteria.companyStage             || '',
    city:                     criteria.city                     || '',
    // Display hint for the searching message - primary title if user stated it, empty if from ICP
    displayTitle:             criteria.displayTitle             || '',
    // 'country' when user said "in the US/nationwide" - bypasses profile hqState filter
    geographyScope:           criteria.geographyScope           || '',
    // The department the user asked for, when they named a team instead of a job title.
    functionScope:            criteria.functionScope            || '',
  };

  // One controller for two reasons: the user pressing Stop, and the idle timeout below giving up.
  // Both need to kill the same socket; only the message differs, and `opts.signal` is what tells
  // them apart afterwards.
  const ctrl = new AbortController();
  const onExternalAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onExternalAbort, { once: true });
  if (opts.signal?.aborted) ctrl.abort();

  // True once the stream has produced anything at all. It decides whether the fallback at the
  // bottom is allowed to fire - see the note there.
  let sawAnyBytes = false;

  try {
    const response = await fetch(`${BACKEND_URL}/api/hunter/search/stream`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify(payload),
      signal:  ctrl.signal,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Stream request failed' }));
      throw new Error(err.error || `Stream failed with status ${response.status}`);
    }

    return await new Promise((resolve, reject) => {
      let finalResult = null;

      // Safety timeout measured from the LAST event, not from the start of the search.
      //
      // It used to be a flat 180s cap on total duration. A cold nationwide search legitimately
      // runs longer than that, so the timeout fired mid-search, the catch below fell back to
      // the non-streaming endpoint, and the whole search ran a second time — while the server,
      // which has no disconnect handler, kept the abandoned first run going. One request for 5
      // contacts became two full searches and ~10 minutes. It was deterministic, not a race:
      // every search over 180s ran twice.
      //
      // Idle-based is the right measure. The backend sends keep-alive pings every 15s, so a
      // stream that is still working always resets this; only genuine silence trips it.
      const IDLE_TIMEOUT_MS = 90_000;
      let streamTimeout = null;
      const failOnIdle = () => {
        // Abort as well as reject. Rejecting alone left the reader looping and, more expensively,
        // left the BACKEND running - which is the other half of the double-search story above:
        // the abandoned first search kept spending while the fallback started a second one. Now
        // giving up here actually hangs up.
        ctrl.abort();
        reject(friendlyError('Search timed out. Please try again.'));
      };
      const resetIdleTimer = () => {
        clearTimeout(streamTimeout);
        streamTimeout = setTimeout(failOnIdle, IDLE_TIMEOUT_MS);
      };
      resetIdleTimer();

      readSSEStream(response, (event) => {
        // Any event at all - including keep-alives - means the search is alive.
        sawAnyBytes = true;
        resetIdleTimer();
        console.log('[SSE] event received:', event.type, event.type === 'done' ? `(${(event.contacts||[]).length} contacts)` : '');
        onEvent(event);
        if (event.type === 'done') {
          clearTimeout(streamTimeout);
          finalResult = {
            contacts: event.contacts || [],
            total:    event.total,
            source:   event.source   || 'Lead411',
            quota:    event.quota    || null,
            credits:  event.credits  || null,      // live lead-credit balance after debit
            creditBlocked: event.creditBlocked || false,
            // What the user actually asked for, and whether their balance cut it short. The
            // backend clamps `limit` to the remaining balance; without these the shortfall is
            // invisible and reads as "the search couldn't find them".
            requestedLimit:       event.requestedLimit || null,
            limitCappedByCredits: event.limitCappedByCredits || false,
            // ...and whether `requestedLimit` was ever requested at all.
            limitStated:          event.limitStated || false,
            // The other half of that story: the search found people and the user's own delivery
            // rules withheld them (already delivered, per-company cap). Picked out explicitly -
            // this object is built field by field, so anything not named here never arrives.
            shortfall:            event.shortfall || null,

            // What the user was actually BILLED, as opposed to how many rows came back.
            //
            // These are not decoration. The cost note used to compute the charge as
            // `contacts.length`, which is only correct when every delivered row is billable - and
            // since the free re-pull shipped it frequently is not. A search that returned five
            // contacts the user already owned told them "Used 5 lead credits" while charging
            // nothing, which is the single worst thing a billing message can do.
            //
            // `null` rather than 0 when the backend did not send it: absent must fall back to the
            // old row-count behaviour, not silently claim the search was free.
            chargedCount: typeof event.chargedCount === 'number' ? event.chargedCount : null,
            // How many came from contacts this user already owns, and from the shared bucket.
            ownedCount:   event.ownedCount || 0,
            bucketRows:   event.bucketRows || 0,
            // Saved contacts dropped because re-verification contradicted them. Only ever
            // populated when the employment gate positively said so - never when it simply did
            // not look - so it can be shown to the user as fact.
            staleDropped: event.staleDropped || [],
            crmSkipped:   event.crmSkipped   || 0,
          };
        } else if (event.type === 'error') {
          clearTimeout(streamTimeout);
          reject(new Error(event.message || 'Search failed'));
        }
      }, () => { sawAnyBytes = true; resetIdleTimer(); }).then(() => {
        clearTimeout(streamTimeout);
        console.log('[SSE] stream complete - finalResult:', finalResult ? `${finalResult.contacts.length} contacts` : 'NULL (no done event)');
        if (finalResult) resolve(finalResult);
        else reject(new Error('Stream ended without result'));
      }).catch((err) => {
        clearTimeout(streamTimeout);
        console.warn('[SSE] stream error:', err.message);
        reject(err);
      });
    });
  } catch (streamErr) {
    // The user pressed Stop. Say so, and say nothing else - in particular, do NOT fall through to
    // the block below, which would answer a cancellation by running the whole search again.
    if (opts.signal?.aborted) {
      const cancelled = new Error('Search stopped.');
      cancelled.cancelled = true;   // a flag, so callers branch on intent rather than on wording
      throw cancelled;
    }

    // SSE stream failed - fall back to the regular (non-streaming) endpoint so contacts still
    // appear when streaming itself is unavailable.
    //
    // Gated on having seen no bytes at all, which is what "streaming is unavailable" actually
    // looks like. Before that gate, ANY late failure re-ran the entire search on an endpoint with
    // no cancellation - so a stream that died three minutes in quietly bought a second set of
    // Lead411 credits and charged for them. A server that dies mid-search now surfaces the error,
    // which is the honest answer.
    if (!sawAnyBytes) {
      console.warn('[Hunter] SSE stream never started, falling back to regular search:', streamErr.message);
      onEvent({ type: 'searching', message: 'Searching…' });
      return searchContacts(criteria, profileFilters, sellerProfile);
    }

    console.warn('[Hunter] SSE stream failed mid-search:', streamErr.message);
    throw streamErr;
  } finally {
    opts.signal?.removeEventListener('abort', onExternalAbort);
  }
}

// ─── Search Lead411 via backend - returns pre-verified { contact, result } pairs

export async function searchContacts(criteria, profileFilters = null, sellerProfile = null) {
  const payload = {
    firstName:                criteria.firstName                || '',
    lastName:                 criteria.lastName                 || '',
    titleKeyword:             criteria.titleKeyword             || '',
    companyDomain:            criteria.companyDomain            || '',
    companyName:              criteria.companyName              || '',
    countryCode:              criteria.countryCode              || 'US',
    stateArray:               criteria.stateArray               || '',
    filterLevel:              criteria.filterLevel              || '',
    limit:                    criteria.limit                    || 10,
    // Whether that number is theirs or ours. The payload is a whitelist, so without this line the
    // flag never arrives and the delivery copy goes back to telling people they asked for 10.
    limitStated:              criteria.limitStated              || false,
    profileFilters,
    excludeEmails:            criteria.excludeEmails            || [],
    bypassCache:              criteria.bypassCache              || false,
    // "New pool". excludeEmails only carries what THIS chat is holding, so on a fresh chat it is
    // empty; this flag is what tells the server to exclude everything it has ever delivered to
    // this user, across every session. The payload is a whitelist - omit it here and the flag
    // never reaches the backend.
    newPool:                  criteria.newPool                  || false,
    sellerProfile,
    userPerCompanyCap:        criteria.userPerCompanyCap        ?? null,
    userIndustryDistribution: criteria.userIndustryDistribution ?? null,
    industryOverride:         criteria.industryOverride         || null,
    // Same as the stream body: the card's people-vs-company answer was dropped on this path.
    locationType:             criteria.locationType             || '',
    employeeRange:            criteria.employeeRange            || null,
    // Explicit revenue figure named in query - overrides profile revenue
    revenueRange:             criteria.revenueRange             || null,
    companyStage:             criteria.companyStage             || '',
    city:                     criteria.city                     || '',
    geographyScope:           criteria.geographyScope           || '',
    // The department the user asked for, when they named a team instead of a job title.
    functionScope:            criteria.functionScope            || '',
  };

  console.log('Sending search payload to backend:', payload);

  const r = await fetch(`${BACKEND_URL}/api/hunter/search`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify(payload),
  });

  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Search request failed' }));
    throw new Error(err.error || `Search failed with status ${r.status}`);
  }

  const d = await r.json();
  console.log(`Search returned ${d.contacts?.length || 0} contacts, source: ${d.source}`);

  // d.contacts is [{ contact, result }] - pre-verified by the backend
  // d.quota holds the real Lead411 remaining credits after unlocking (may be null for cache hits)
  //
  // This is the fallback the stream drops to, and it must return the SAME SHAPE - one consumer
  // (VerifierPage.executeSearch) reads both through the same binding. It had already drifted:
  // `total`, `requestedLimit`, `limitCappedByCredits` and `limitStated` were named in the stream
  // handler and missing here, so a search that fell back silently lost the reason its list was
  // short. Anything added to one must be added to the other; the drift is invisible until a
  // stream fails, which is exactly when nobody is watching.
  return {
    contacts: d.contacts || [], source: d.source || 'Lead411', quota: d.quota || null,
    credits: d.credits || null, creditBlocked: d.creditBlocked || false,
    shortfall: d.shortfall || null,
    total: d.total,
    requestedLimit: d.requestedLimit || null,
    limitCappedByCredits: d.limitCappedByCredits || false,
    limitStated: d.limitStated || false,
    chargedCount: typeof d.chargedCount === 'number' ? d.chargedCount : null,
    ownedCount: d.ownedCount || 0,
    bucketRows: d.bucketRows || 0,
    staleDropped: d.staleDropped || [],
    crmSkipped:   d.crmSkipped   || 0,
  };
}

// ─── Search Presets ───────────────────────────────────────────────────────────

export async function fetchPresets() {
  const r = await fetch(`${BACKEND_URL}/api/presets`, { headers: await authedHeaders() });
  if (!r.ok) return [];
  const d = await r.json().catch(() => ({ presets: [] }));
  return d.presets || [];
}

export async function savePreset(name, criteria) {
  const r = await fetch(`${BACKEND_URL}/api/presets`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ name, criteria }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Save preset failed' }));
    throw new Error(err.error || 'Save preset failed');
  }
  return r.json(); // { preset }
}

export async function deletePreset(id) {
  const r = await fetch(`${BACKEND_URL}/api/presets/${id}`, {
    method:  'DELETE',
    headers: await authedHeaders(),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Delete preset failed' }));
    throw new Error(err.error || 'Delete preset failed');
  }
  return r.json();
}

// ─── LinkedIn Lookup ──────────────────────────────────────────────────────────

export async function linkedinLookup({ url, slug }) {
  const r = await fetch(`${BACKEND_URL}/api/linkedin/lookup`, {
    method:  'POST',
    headers: await authedHeaders(),
    body:    JSON.stringify({ url, slug }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'LinkedIn lookup failed' }));
    throw new Error(err.error || `LinkedIn lookup failed with status ${r.status}`);
  }
  return r.json(); // { found, contact?, result?, source?, profile?, message? }
}

export async function linkedinBatchDraft({ contacts, sellerProfile = null }) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 300000); // 5 min - covers many parallel Anthropic calls
  try {
    const r = await fetch(`${BACKEND_URL}/api/linkedin/batch-draft`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ contacts, sellerProfile }),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: 'Batch draft failed' }));
      throw new Error(err.error || `Batch draft failed with status ${r.status}`);
    }
    return r.json(); // { drafts: { [email]: draftText | null } }
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw friendlyError('Batch draft timed out. Please try again.');
    throw err;
  }
}

export async function linkdapiBatchEmpVerify(contacts) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 120000); // 2 min
  try {
    const r = await fetch(`${BACKEND_URL}/api/linkdapi/batch-emp-verify`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ contacts }),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: 'Batch employment verify failed' }));
      throw new Error(err.error || `Batch employment verify failed with status ${r.status}`);
    }
    return r.json(); // { results: { [email]: { confirmed, currentCompany, currentTitle, currentLocation, fromCache } }, remaining, creditsSpent }
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw new Error('Batch employment verify timed out.');
    throw err;
  }
}

// What a company does and whether it fits the seller's ICP. Free. Resolves to the backend's
// { name, domain, whatTheyDo, fit, fitReason, ... } or { notFound: true, name, domain }.
export async function companyResearch({ company, domain = '', companyLinkedinUrl = '', sellerProfile = null }) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 90000); // the full profile can run ~45s on a cold company
  try {
    const r = await fetch(`${BACKEND_URL}/api/company/research`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ company, domain, companyLinkedinUrl, sellerProfile }),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: 'Company research failed' }));
      throw new Error(err.error || `Company research failed with status ${r.status}`);
    }
    return r.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw friendlyError('Company research timed out. Please try again.');
    throw err;
  }
}

// A person's LinkedIn profile from their name and company, for research by name. Free.
// Resolves to { found: true, url, slug, title, company } or { found: false, reason }.
export async function findPerson({ name, company = '' }) {
  return postJson('/api/linkedin/find-person', { name, company }, 45000, 'Profile lookup');
}

// Who holds one of these titles at a company, from LinkedIn ("their manager"). Free.
// Resolves to { people: [{ name, title, linkedinUrl }], domain }.
export async function findRole({ company, domain = '', titles }) {
  return postJson('/api/linkedin/find-role', { company, domain, titles }, 60000, 'Role lookup');
}

async function postJson(path, body, timeoutMs, label) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const r = await fetch(`${BACKEND_URL}${path}`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify(body),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: `${label} failed` }));
      throw new Error(err.error || `${label} failed with status ${r.status}`);
    }
    return r.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw friendlyError(`${label} timed out. Please try again.`);
    throw err;
  }
}

export async function linkedinResearch({ profile, sellerProfile = null, instruction = '' }) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 90000); // 90s - covers LinkdAPI + Anthropic + retries
  try {
    const r = await fetch(`${BACKEND_URL}/api/linkedin/research`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ profile, sellerProfile, instruction }),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: 'LinkedIn research failed' }));
      throw new Error(err.error || `LinkedIn research failed with status ${r.status}`);
    }
    return r.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw friendlyError('Research timed out. Please try again.');
    throw err;
  }
}

// Rewrite a drafted email into a chosen tone. Free/unmetered on the backend.
// Returns { subject, body }.
export async function retoneEmail({ subject = '', body, tone, sellerProfile = null }) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 45000); // 45s - one short Anthropic rewrite
  try {
    const r = await fetch(`${BACKEND_URL}/api/email/retone`, {
      method:  'POST',
      headers: await authedHeaders(),
      body:    JSON.stringify({ subject, body, tone, sellerProfile }),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: 'Retone failed' }));
      throw new Error(err.error || `Retone failed with status ${r.status}`);
    }
    return r.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') throw friendlyError('Retone timed out. Please try again.');
    throw err;
  }
}
